import React, { useCallback } from "react";
import { Button } from "@design-system/Button/Button";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useStripeExpressLoginLinkMutation } from "@mutations/stripeConnection";

const POPUP_BLOCKED_TOAST =
    "Allow popups to open the Stripe Express dashboard.";

interface Props {
    connectionId: number;
}

function openBlankWindow(): Window | null {
    const dashboardWindow = window.open("", "_blank");
    if (!dashboardWindow) {
        return null;
    }
    dashboardWindow.opener = null;
    return dashboardWindow;
}

export const StripeExpressDashboardButton: React.FC<Props> = ({
    connectionId,
}) => {
    const { toast } = useToaster();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const mutation = useStripeExpressLoginLinkMutation();

    const handleClick = useCallback(async () => {
        if (refuseIfViewOnly()) {
            return;
        }

        const dashboardWindow = openBlankWindow();
        try {
            const { url } = await mutation.mutateAsync(connectionId);
            if (!dashboardWindow) {
                toast(POPUP_BLOCKED_TOAST);
                return;
            }
            dashboardWindow.location.href = url;
        } catch {
            dashboardWindow?.close();
        }
    }, [connectionId, mutation, refuseIfViewOnly, toast]);

    return (
        <Button
            variant="primary"
            size="md"
            disabled={mutation.isPending}
            onClick={handleClick}
        >
            Stripe Express dashboard
        </Button>
    );
};
